// src/pages/Menu/MenuItemManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Combobox } from "@headlessui/react";
import api from "@/lib/axios"; // baseURL -> /solo-mdsh-api

// ---------- debounce ----------
function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

// ---------- Unit dictionaries & helpers ----------
const MASS = { mg: 0.001, g: 1, kg: 1000, oz: 28.349523125, lb: 453.59237 };
const VOLUME = { ml: 1, l: 1000, floz: 29.5735 };

function unitFamily(u) {
  const key = String(u || "").toLowerCase().replace(/\s+/g, "");
  if (key in MASS) return "mass";
  if (key in VOLUME) return "volume";
  if (key === "each") return "each";
  if (key === "l") return "volume";
  if (key === "fl oz" || key === "floz" || key === "fl-oz") return "volume";
  return null;
}

function normalizeUnit(u) {
  if (!u) return null;
  const key = String(u).toLowerCase().replace(/\s+/g, "");
  if (key === "floz" || key === "fl-oz" || key === "fl" || key === "floz.") return "floz";
  if (key === "l" || key === "lt" || key === "liter" || key === "litre") return "l";
  if (key === "ounce") return "oz";
  if (key === "pound") return "lb";
  if (key === "grams") return "g";
  if (key === "milliliter" || key === "millilitre") return "ml";
  if (key === "pcs" || key === "piece" || key === "pc") return "each";
  return key;
}

/** Parse "1kg", "12x1kg", "24 x 330ml", "24 each" -> { qtyTotal, unit, family } */
function parseUnitSize(str) {
  if (!str) return null;
  const s = String(str).toLowerCase().replace(/×/g, "x").trim();

  // 12x1kg / 24 x 330ml
  const multi = s.match(/(\d+)\s*x\s*(\d+(?:\.\d+)?)\s*([a-z\s\.]+)/);
  if (multi) {
    const packs = Number(multi[1]);
    const innerQty = Number(multi[2]);
    const unit = normalizeUnit(multi[3]);
    const fam = unitFamily(unit);
    if (packs > 0 && innerQty > 0 && fam) return { qtyTotal: packs * innerQty, unit, family: fam };
  }

  // 1kg / 330ml / 24 each
  const single = s.match(/(\d+(?:\.\d+)?)\s*([a-z\s\.]+)/);
  if (single) {
    const qty = Number(single[1]);
    const unit = normalizeUnit(single[2]);
    const fam = unitFamily(unit);
    if (qty > 0 && fam) return { qtyTotal: qty, unit, family: fam };
  }
  return null;
}

/** Convert a quantity to base units (g, ml, or each) */
function toBase(family, qty, unit) {
  if (family === "mass") return qty * MASS[unit];
  if (family === "volume") return qty * VOLUME[unit];
  if (family === "each") return qty;
  return NaN;
}

// ---------- UI helpers ----------
const numberOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

function currency(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "£0.00";
  return `£${Number(v).toFixed(2)}`;
}

function moneyToNumber(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const cleaned = v.replace(/[^0-9.\-]/g, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function toNumSafe(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

// ---------- API path constants ----------
const STOCK_ITEMS_BASE = "/inventory/stock-items";
const MENU_ITEMS_BASE = "/inventory/menu-items";

// Try endpoints in order (prevents env mismatch crashes)
async function apiGetFirst(paths, config) {
  let lastErr = null;
  for (const p of paths) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await api.get(p, config);
    } catch (e) {
      lastErr = e;
      // keep trying
    }
  }
  throw lastErr;
}

async function apiPostFirst(paths, body, config) {
  let lastErr = null;
  for (const p of paths) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await api.post(p, body, config);
    } catch (e) {
      lastErr = e;
      // keep trying
    }
  }
  throw lastErr;
}

// ---------- Component ----------
const MenuItemManager = () => {
  const [menuItems, setMenuItems] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    default_serving_size: 1,
    manual_cost_override: "",
    manual_sale_price_override: "",
    suggested_selling_price: "",
    actual_selling_price: "",
    ingredients: [],
  });

  const [stockItems, setStockItems] = useState([]);
  const [filteredStockItems, setFilteredStockItems] = useState([]);
  const [stockQuery, setStockQuery] = useState("");
  const debouncedQuery = useDebounced(stockQuery, 300);
  const [isSearching, setIsSearching] = useState(false);

  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState([]);
  const [calculatedAutoCost, setCalculatedAutoCost] = useState(0);

  useEffect(() => {
    fetchMenuItems();
    preloadStockItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMenuItems = async () => {
    try {
      const res = await apiGetFirst([MENU_ITEMS_BASE], {});
      setMenuItems(Array.isArray(res.data) ? res.data : res.data?.rows || res.data?.items || []);
    } catch (e) {
      console.error("Failed to fetch menu items", e);
      setMenuItems([]);
    }
  };

  // initial preload (no q)
  const preloadStockItems = async () => {
    try {
      const res = await api.get(STOCK_ITEMS_BASE, { params: { limit: 200 } });
      let list = Array.isArray(res.data) ? res.data : res.data?.rows || res.data?.items || [];
      list = list.filter((it) => (moneyToNumber(it.unit_price) ?? 0) > 0);
      setStockItems(list);
      setFilteredStockItems(list);
    } catch (e) {
      console.error("Failed to preload stock items", e);
      setStockItems([]);
      setFilteredStockItems([]);
    }
  };

  // debounced server search using ?q=
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.trim().length < 2) {
      setFilteredStockItems(stockItems);
      setIsSearching(false);
      return;
    }

    let cancelled = false;
    setIsSearching(true);

    api
      .get(STOCK_ITEMS_BASE, { params: { q: debouncedQuery, limit: 25 } })
      .then((res) => {
        if (cancelled) return;
        let list = Array.isArray(res.data) ? res.data : res.data?.rows || res.data?.items || [];
        list = list.filter((it) => (moneyToNumber(it.unit_price) ?? 0) > 0);
        setFilteredStockItems(list);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("Stock search failed; falling back to local filter", err);
        const q = debouncedQuery.toLowerCase();
        setFilteredStockItems(
          stockItems.filter((item) => {
            const code = String(item.item_code || "").toLowerCase();
            const desc = String(item.description || "").toLowerCase();
            return code.includes(q) || desc.includes(q);
          })
        );
      })
      .finally(() => !cancelled && setIsSearching(false));

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, stockItems]);

  // ingredient mutators
  const addIngredient = () => {
    setFormData((prev) => ({
      ...prev,
      ingredients: [...prev.ingredients, { item_id: null, quantity: 1, unit: "g", unit_cost_override: "" }],
    }));
  };

  const removeIngredient = (idx) => {
    setFormData((prev) => {
      const copy = [...prev.ingredients];
      copy.splice(idx, 1);
      return { ...prev, ingredients: copy };
    });
  };

  const setIngredient = (idx, patch) => {
    setMessage("");
    setFormData((prev) => {
      const copy = [...prev.ingredients];
      copy[idx] = { ...copy[idx], ...patch };
      return { ...prev, ingredients: copy };
    });
  };

  // costing + validation
  const lineDetails = useMemo(() => {
    return formData.ingredients.map((ing) => {
      const stock =
        (ing.item_id &&
          (filteredStockItems.find((s) => s.item_id === Number(ing.item_id)) ||
            stockItems.find((s) => s.item_id === Number(ing.item_id)))) ||
        null;

      const issues = [];
      if (!stock) return { issues: ["Select an item from stock."], cost: 0, ok: false };

      const unitPrice = moneyToNumber(stock.unit_price);
      const unitSizeStr = stock.unit_size;
      const overridePerUnit = numberOrNull(ing.unit_cost_override);

      const parsed = parseUnitSize(unitSizeStr);
      let perBaseUnit = null;
      let stockFamily = null;

      if (unitPrice && parsed) {
        stockFamily = parsed.family;
        const stockBaseQty = toBase(stockFamily, parsed.qtyTotal, parsed.unit);
        if (stockBaseQty > 0) perBaseUnit = unitPrice / stockBaseQty;
      }

      const ingUnit = normalizeUnit(ing.unit);
      const ingFam = unitFamily(ingUnit);

      if (!ingFam) issues.push("Select a valid unit.");
      if (ingFam && stockFamily && ingFam !== stockFamily) issues.push("Unit type mismatch (mass vs volume vs each).");

      // If we can't compute from stock item, require override
      if (overridePerUnit === null && (!unitPrice || !parsed || !perBaseUnit)) {
        issues.push("Stock item missing parsable unit/price. Enter per-unit override.");
      }

      const qty = toNumSafe(ing.quantity, 0);
      if (!(qty > 0)) issues.push("Quantity must be greater than 0.");

      let lineCost = 0;
      if (issues.length === 0 && ingFam) {
        if (overridePerUnit !== null) {
          lineCost = qty * overridePerUnit;
        } else {
          const qtyInBase =
            ingFam === "mass" ? qty * MASS[ingUnit] : ingFam === "volume" ? qty * VOLUME[ingUnit] : qty; // each
          lineCost = qtyInBase * perBaseUnit;
        }
      }

      return { issues, cost: lineCost, ok: issues.length === 0, stock, ingUnit };
    });
  }, [formData.ingredients, filteredStockItems, stockItems]);

  useEffect(() => {
    const total = lineDetails.reduce((a, l) => a + (l.cost || 0), 0);
    setCalculatedAutoCost(total);
    setErrors(
      lineDetails
        .map((l, i) => (l.issues.length ? `Ingredient ${i + 1}: ${l.issues.join(" ")}` : null))
        .filter(Boolean)
    );
  }, [lineDetails]);

  const foodCostPct = useMemo(() => {
    const sp = numberOrNull(formData.actual_selling_price);
    if (!sp || sp <= 0) return null;
    return (calculatedAutoCost / sp) * 100;
  }, [calculatedAutoCost, formData.actual_selling_price]);

  const onField = (e) => {
    const { name, value } = e.target;
    setMessage("");
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const canSave = formData.name.trim().length > 0 && formData.ingredients.length > 0 && errors.length === 0;

  const onSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!canSave) {
      setMessage("❌ Fix the ingredient errors before saving.");
      return;
    }

    const payload = {
      name: formData.name.trim(),
      description: formData.description,
      default_serving_size: Number(formData.default_serving_size) || 1,
      manual_cost_override: numberOrNull(formData.manual_cost_override),
      manual_sale_price_override: numberOrNull(formData.manual_sale_price_override),

      ingredients: formData.ingredients.map((ing, idx) => {
        const line = lineDetails[idx] || { cost: 0 };
        const uom = normalizeUnit(ing.unit) || "unit";

        return {
          item_id: Number(ing.item_id),
          quantity_required: toNumSafe(ing.quantity, 0),

          // ✅ IMPORTANT: backend expects this key
          quantity_uom: uom,

          // compat alias (won’t hurt if backend ignores it)
          unit_of_measure: uom,

          unit_cost_override: numberOrNull(ing.unit_cost_override),

          // UI-only diagnostic; backend will ignore unless you store it
          derived_line_cost: Number((line.cost || 0).toFixed(4)),
        };
      }),

      pricing: {
        auto_cost: Number(calculatedAutoCost.toFixed(4)),
        manual_cost_override: numberOrNull(formData.manual_cost_override),
        suggested_selling_price: numberOrNull(formData.suggested_selling_price),
        actual_selling_price: numberOrNull(formData.actual_selling_price),
      },
    };

    try {
      await apiPostFirst([MENU_ITEMS_BASE], payload);

      setMessage("✅ Menu item saved successfully");
      setFormData({
        name: "",
        description: "",
        default_serving_size: 1,
        manual_cost_override: "",
        manual_sale_price_override: "",
        suggested_selling_price: "",
        actual_selling_price: "",
        ingredients: [],
      });
      setCalculatedAutoCost(0);
      fetchMenuItems();
    } catch (err) {
      console.error(err);
      const apiMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to save menu item";
      setMessage(`❌ ${apiMsg}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50 py-8 text-slate-900">
      <div className="max-w-5xl mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6">
          <h2 className="text-2xl font-semibold tracking-tight flex items-center gap-2 text-slate-900">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
            Menu Builder
          </h2>

          {message && (
            <div className="mt-4 text-sm px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-700">
              {message}
            </div>
          )}

          {errors.length > 0 && (
            <div className="mt-4 text-sm px-3 py-2 rounded-xl bg-rose-50 border border-rose-200 text-rose-700">
              <ul className="list-disc ml-5 space-y-1">
                {errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-6">
            {/* Basics */}
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={onField}
                placeholder="Dish/Drink Name"
                className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
                required
              />
              <input
                type="number"
                name="default_serving_size"
                value={formData.default_serving_size}
                onChange={onField}
                min={1}
                placeholder="Default Serving Size"
                className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
              />
            </div>

            <textarea
              name="description"
              value={formData.description}
              onChange={onField}
              placeholder="Description"
              className="bg-white border border-slate-200 rounded-xl px-3 py-2 w-full min-h-[88px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
            />

            {/* Pricing */}
            <div className="grid md:grid-cols-2 gap-4">
              <input
                type="number"
                name="manual_cost_override"
                value={formData.manual_cost_override}
                onChange={onField}
                placeholder="Manual Cost Override (total recipe)"
                step="0.01"
                min="0"
                className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
              />
              <input
                type="number"
                name="manual_sale_price_override"
                value={formData.manual_sale_price_override}
                onChange={onField}
                placeholder="Manual Sale Price Override"
                step="0.01"
                min="0"
                className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
              />
              <input
                type="number"
                name="suggested_selling_price"
                value={formData.suggested_selling_price}
                onChange={onField}
                placeholder="Suggested Price"
                step="0.01"
                min="0"
                className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
              />
              <input
                type="number"
                name="actual_selling_price"
                value={formData.actual_selling_price}
                onChange={onField}
                placeholder="Actual Selling Price"
                step="0.01"
                min="0"
                className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
              />
            </div>

            {/* Ingredients */}
            <div>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">Ingredients</h3>
                <button
                  type="button"
                  onClick={addIngredient}
                  className="text-emerald-700 hover:text-emerald-800 text-sm font-medium"
                >
                  + Add Ingredient
                </button>
              </div>

              <div className="mt-3 space-y-3">
                {formData.ingredients.map((ing, idx) => {
                  const selected = stockItems.find((s) => s.item_id === Number(ing.item_id)) || null;
                  const line = lineDetails[idx];

                  return (
                    <div
                      key={idx}
                      className="grid grid-cols-12 gap-3 items-start bg-slate-50 border border-slate-200 rounded-xl p-3"
                    >
                      {/* Stock item selector */}
                      <div className="col-span-12 md:col-span-5">
                        <Combobox value={selected} onChange={(val) => setIngredient(idx, { item_id: val.item_id })}>
                          <div className="relative">
                            <Combobox.Input
                              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
                              placeholder="Search by code or description"
                              onChange={(e) => setStockQuery(e.target.value)}
                              displayValue={(item) => (item ? `${item.item_code} — ${item.description ?? ""}` : "")}
                            />
                            <Combobox.Options className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg">
                              {isSearching && <div className="px-3 py-2 text-sm text-slate-500">Searching…</div>}
                              {!isSearching && filteredStockItems.length === 0 && (
                                <div className="px-3 py-2 text-sm text-slate-500">No items found.</div>
                              )}
                              {filteredStockItems.map((item) => {
                                const priceNum = moneyToNumber(item.unit_price);
                                const hasPrice = priceNum !== null && priceNum > 0;
                                return (
                                  <Combobox.Option
                                    key={item.item_id}
                                    value={item}
                                    disabled={!hasPrice}
                                    className={`px-3 py-2 text-sm cursor-pointer ${
                                      hasPrice ? "hover:bg-emerald-50 text-slate-800" : "opacity-50 cursor-not-allowed text-slate-500"
                                    }`}
                                  >
                                    <div className="flex justify-between gap-3">
                                      <span>
                                        {item.item_code} — {item.description}
                                      </span>
                                      <span className="text-xs text-slate-500 whitespace-nowrap">
                                        {hasPrice ? `${currency(priceNum)} / ${item.unit_size ?? "unit"}` : "⚠ No price"}
                                      </span>
                                    </div>
                                  </Combobox.Option>
                                );
                              })}
                            </Combobox.Options>
                          </div>
                        </Combobox>
                        <p className="text-xs text-slate-500 mt-1">
                          {selected
                            ? `Stock UoM: ${selected.unit_size ?? "—"} | Price: ${currency(moneyToNumber(selected.unit_price) ?? 0)}`
                            : "Select a stocked item (priced only)."}
                        </p>
                      </div>

                      {/* Qty + Unit */}
                      <div className="col-span-6 md:col-span-3">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={ing.quantity}
                          onChange={(e) => setIngredient(idx, { quantity: e.target.value })}
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
                          placeholder="Qty"
                        />
                      </div>

                      <div className="col-span-6 md:col-span-2">
                        <select
                          value={ing.unit}
                          onChange={(e) => setIngredient(idx, { unit: e.target.value })}
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
                        >
                          <optgroup label="Mass">
                            <option value="mg">mg</option>
                            <option value="g">g</option>
                            <option value="kg">kg</option>
                            <option value="oz">oz</option>
                            <option value="lb">lb</option>
                          </optgroup>
                          <optgroup label="Volume">
                            <option value="ml">ml</option>
                            <option value="l">L</option>
                            <option value="floz">fl oz</option>
                          </optgroup>
                          <optgroup label="Each">
                            <option value="each">each</option>
                          </optgroup>
                        </select>
                      </div>

                      {/* Per-unit override + line figures */}
                      <div className="col-span-12 md:col-span-2">
                        <input
                          type="number"
                          min="0"
                          step="0.0001"
                          value={ing.unit_cost_override}
                          onChange={(e) => setIngredient(idx, { unit_cost_override: e.target.value })}
                          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300"
                          placeholder="Override / unit"
                          title="Per selected unit override (e.g., £ per g/ml/each)"
                        />
                        <div className="text-xs text-slate-500 mt-1">
                          Line: <strong className="text-slate-800">{currency(line?.cost || 0)}</strong>
                        </div>
                      </div>

                      {/* Remove */}
                      <div className="col-span-12 md:col-span-1 flex md:block">
                        <button
                          type="button"
                          onClick={() => removeIngredient(idx)}
                          className="ml-auto md:ml-0 md:mt-1 px-3 py-2 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50"
                          title="Remove ingredient"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Totals */}
            <div className="flex flex-wrap items-center gap-4 justify-between bg-emerald-50 border border-emerald-100 rounded-xl p-4">
              <div className="text-sm text-slate-700">
                📊 Auto-calculated ingredient cost: <strong className="text-slate-900">{currency(calculatedAutoCost)}</strong>
              </div>
              {foodCostPct !== null && (
                <div className="text-sm text-slate-700">
                  Food Cost %: <strong className="text-slate-900">{foodCostPct.toFixed(1)}%</strong>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={!canSave}
                className={`px-4 py-2 rounded-xl text-white ${
                  canSave ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-300 cursor-not-allowed"
                }`}
              >
                Save Menu Item
              </button>
              {!canSave && <span className="text-xs text-slate-500">Resolve errors to enable Save.</span>}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MenuItemManager;